"use client";

import { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
export default function TheStoryBehindTheCoach() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const progress = (viewportH - rect.top) / (viewportH + rect.height);
      setOffset(progress * 100);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const paragraphs = ["We exist for the movement. For the single instant when effort, focus, and biology converge inside a training session and limits begin to dissolve. Coach Kira Fit is not a gym. It is a discipline — a refusal of the generic program and a quiet insistence that fitness, in its purest form, remains an act of personal transformation.", "Our founding question was simple. What happens to the human body when it is stripped of distraction, of guesswork, of the relentless noise of fitness trends? We believe the answer is optimization — not only of the physical form, but of the mindset that drives it.", "Every movement prescribed within these sessions has been chosen with intent. We seek out the functional, the foundational, the science-backed protocols that modern life briefly forgot. We refine them rep by rep, set by set, until the original potential of the athlete breathes once more.", "We do not chase trends. We chase results. The intensity of strength, the precision of form, the quiet resilience of consistency — these are not fads. They are unfinished conversations, and we offer the space in which to continue them.", "Our clients are not just trainees. They are participants in a high-performance ritual. They arrive with purpose, they leave with clarity, and between those two states we offer everything we have — coaching, technique, accountability, data, and the company of those who still believe a body deserves a lifetime of care.", "We are deliberately focused. We will remain so. Scale is not strategy here; it is the precondition of attention. There is no algorithm at Coach Kira Fit. There is only the coach, the plan, and the progress.", "To start is to accept the terms of the journey. To stay is to remember why human movement was ever called potential in the first place."];
  const firstWord = paragraphs[0].slice(1);
  return <section data-section-id="2828" id="the-story-behind-the-coach" ref={sectionRef} className="relative overflow-hidden bg-background text-foreground py-[200px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-end justify-end overflow-hidden select-none" style={{
      transform: `translate3d(0, ${offset * 0.3 - 15}px, 0)`
    }}>
        <span className="font-default font-bold leading-none translate-x-[8%] translate-y-[15%]" style={{
        fontSize: "30vw",
        color: "hsl(240 5% 90%)",
        letterSpacing: "-0.06em"
      }}>
          KIRA
        </span>
      </div>

      {}
      <div className="relative z-10 px-6 md:px-12">
        <div className="max-w-[720px] mx-auto md:ml-[max(1.5rem,calc(50%-540px))] md:mr-auto">
          {}
          

          {}
          <h2 className="font-default font-bold leading-[0.95] mb-20" style={{
          fontSize: "clamp(3rem, 7vw, 7rem)",
          letterSpacing: "-0.04em"
        }}>
            <span className="block">We exist</span>
            <span className="block">for the</span>
            <span className="block relative inline-block">
              <span className="relative inline-block pb-2" style={{
              backgroundImage: "linear-gradient(var(--primary), var(--primary))",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "0 100%",
              backgroundSize: "100% 8px"
            }}>
                movement.
              </span>
            </span>
          </h2>

          {}
          <div className="font-default" style={{
          color: "var(--muted-foreground)"
        }}>
            {paragraphs.map((paragraph, i) => <div data-index={i} key={i}>
                {i === 0 ? <p className="relative" style={{
              fontSize: "19px",
              lineHeight: 1.7
            }}>
                    <span className="float-left font-default font-bold mr-3 mt-1 leading-[0.85]" style={{
                fontSize: "96px",
                color: "var(--primary)"
              }}>
                      {paragraphs[0].charAt(0)}
                    </span>
                    {firstWord.split(/(optimization|transformation)/).map((chunk, idx) => {
                if (chunk === "optimization" || chunk === "transformation") {
                  return <span key={idx} className="pb-0.5" style={{
                    backgroundImage: "linear-gradient(var(--primary), var(--primary))",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "0 100%",
                    backgroundSize: "100% 2px"
                  }}>
                            {chunk}
                          </span>;
                }
                return <span key={idx}>{chunk}</span>;
              })}
                  </p> : <p className="mt-6" style={{
              fontSize: "19px",
              lineHeight: 1.7
            }}>
                    {i === 2 && paragraph.split(/(refine them rep by rep)/).map((chunk, idx) => chunk === "refine them rep by rep" ? <span key={idx} className="pb-0.5" style={{
                backgroundImage: "linear-gradient(var(--primary), var(--primary))",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "0 100%",
                backgroundSize: "100% 2px"
              }}>
                            {chunk}
                          </span> : <span key={idx}>{chunk}</span>)}
                    {i === 5 && paragraph.split(/(Scale is not strategy here)/).map((chunk, idx) => chunk === "Scale is not strategy here" ? <span key={idx} className="pb-0.5" style={{
                backgroundImage: "linear-gradient(var(--primary), var(--primary))",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "0 100%",
                backgroundSize: "100% 2px"
              }}>
                            {chunk}
                          </span> : <span key={idx}>{chunk}</span>)}
                    {i !== 2 && i !== 5 && paragraph}
                  </p>}

                {}
                {(i + 1) % 2 === 0 && i !== paragraphs.length - 1 && <div className="flex my-10">
                    <Separator className="!w-16 !h-px" style={{
                backgroundColor: "var(--primary)"
              }} />
                  </div>}
              </div>)}

            {}
            <div className="mt-16">
              <p className="font-default italic" style={{
              fontSize: "19px",
              lineHeight: 1.7
            }}>
                Kira Vance, Founder & Head Coach
              </p>
              <p className="font-mono text-xs mt-3" style={{
              color: "var(--muted-foreground)",
              letterSpacing: "0.15em"
            }}>
                EST. 2014, LONDON
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}